import React, { useState, useEffect } from "react";
import axios from "axios";
import Menu from "../Menu";
import { useNavigate } from "react-router-dom";
import SuccessMessage from "../../components/successMessage";
import FailedMessage from "../../components/FailedMessage";
import Switch from "../../components/Switch";
import useApiUrls from "../../useApiUrls";
import Pagination from "../components/Pagination";

const SubGroup = () => {
  const [data, setData] = useState([]);
  const [dataLimit, setDataLimit] = useState(100);
  const [count, setCount] = useState(0);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalItems, setTotalItems] = useState(0);

  const navigate = useNavigate();
  const { getUrlByOperation } = useApiUrls();

  const token = getCookie("token");
  const userid = getCookie("userid");

  // Allocation State
  const [showAllocation, setShowAllocation] = useState(false);
  const [allocationType, setAllocationType] = useState("POS");
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [leftList, setLeftList] = useState([]);
  const [rightList, setRightList] = useState([]);
  const [selectedLeft, setSelectedLeft] = useState([]);
  const [selectedRight, setSelectedRight] = useState([]);

  // SubGroup dropdown state
  const [subGroupOptions, setSubGroupOptions] = useState([]);
  const [selectedSubGroupId, setSelectedSubGroupId] = useState("");
  const [originalRightList, setOriginalRightList] = useState([]);
  const [allAvailableItems, setAllAvailableItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  /* ================= FETCH SUBGROUPS FOR DROPDOWN ================= */
  const fetchSubGroups = async (groupId, availableItems = null) => {
    // Use passed availableItems or fall back to state
    const itemsToUse = availableItems || allAvailableItems;

    try {
      const res = await axios.post(
        "https://api.dqr-display-tms.bonrix.in/api/SubGroupOperation/Search",
        {
          id: 0,
          groupid: groupId,
          ispageload: true,
          pagesize: 1000,
          currentpageno: 0,
          withoutpagination: true,
          pagedirection: "string",
        },
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            "Content-Type": "application/json-patch+json",
            accept: "text/plain",
          },
        }
      );
      const subgroups = res.data?.data || [];
      setSubGroupOptions(subgroups);

      // Auto-select first subgroup if available
      if (subgroups.length > 0) {
        setSelectedSubGroupId(subgroups[0].id);
        fetchAllocatedItems(subgroups[0].id, itemsToUse);
      } else {
        setSelectedSubGroupId("");
        setRightList([]);
        setOriginalRightList([]);
        setLeftList(itemsToUse);
      }
    } catch (error) {
      console.error("Error fetching subgroups:", error);
      setMessage("Failed to fetch subgroups");
    }
  };

  /* ================= FETCH ALLOCATED ITEMS FOR SELECTED SUBGROUP ================= */
  const fetchAllocatedItems = async (subgroupId, availableItems = null) => {
    // Use passed availableItems or fall back to state
    const itemsToFilter = availableItems || allAvailableItems;

    if (!subgroupId) {
      setRightList([]);
      setOriginalRightList([]);
      setLeftList(itemsToFilter);
      return;
    }

    try {
      const res = await axios.post(
        "https://api.dqr-display-tms.bonrix.in/api/SubGroupDataOperation/Search",
        {
          subgroupid: parseInt(subgroupId),
          ispageload: true,
          pagesize: 1000,
          currentpageno: 0,
          withoutpagination: true,
          pagedirection: "string",
        },
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            "Content-Type": "application/json-patch+json",
            accept: "text/plain",
          },
        }
      );
      const allocatedData = res.data?.data || [];
      setRightList(allocatedData);
      setOriginalRightList(allocatedData);

      // Filter left list: show only items that are NOT in allocated data
      // Use posid from allocated data (not id, which is the allocation record ID)
      const allocatedPosIds = allocatedData.map(item => item.posid);
      const filteredLeft = itemsToFilter.filter(
        item => !allocatedPosIds.includes(item.id)
      );
      setLeftList(filteredLeft);

    } catch (error) {
      console.error("Error fetching allocated items:", error);
      setMessage("Failed to fetch allocated items");
    }
  };

  /* ================= FETCH ALL AVAILABLE ITEMS ================= */
  const fetchAllAvailableItems = async () => {
    try {
      const res = await axios.post(
        "https://api.dqr-display-tms.bonrix.in/api/POSOperation/Search",
        {
          ispageload: true,
          pagesize: 1000,
          currentpageno: 0,
          withoutpagination: true,
          pagedirection: "string",
        },
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            "Content-Type": "application/json-patch+json",
            accept: "text/plain",
          },
        }
      );
      const allItems = res.data?.data || [];
      setAllAvailableItems(allItems);
      setLeftList(allItems);
      return allItems; // Return items for immediate use
    } catch (error) {
      console.error("Error fetching available items:", error);
      setMessage("Failed to fetch available items");
      return [];
    }
  };

  /* ================= HANDLE SUBGROUP SELECTION ================= */
  const handleSubGroupChange = (subgroupId) => {
    setSelectedSubGroupId(subgroupId);
    setSelectedLeft([]);
    setSelectedRight([]);
    fetchAllocatedItems(subgroupId);
  };

  /* ================= GET DISPLAY TEXT ================= */
  const getDisplayText = (item) => {
    const posName = item.posname || item.name || "N/A";
    const merchantName = item.merchantid || "N/A";
    return `${posName}, ${merchantName}`;
  };

  /* ================= MOVE LOGIC ================= */
  const moveRight = () => {
    if (selectedLeft.length === 0) return;

    const itemsToMove = leftList.filter(item => selectedLeft.includes(item.id));
    const newRightList = [...rightList, ...itemsToMove];
    const newLeftList = leftList.filter(item => !selectedLeft.includes(item.id));

    setRightList(newRightList);
    setLeftList(newLeftList);
    setSelectedLeft([]);
  };

  const moveLeft = () => {
    if (selectedRight.length === 0) return;

    const itemsToMove = rightList.filter(item => selectedRight.includes(item.id));
    const newLeftList = [...leftList, ...itemsToMove];
    const newRightList = rightList.filter(item => !selectedRight.includes(item.id));

    setLeftList(newLeftList);
    setRightList(newRightList);
    setSelectedRight([]);
  };

  /* ================= DRAG & DROP ================= */
  const onDragStart = (e, item) => {
    e.dataTransfer.setData("item", JSON.stringify(item));
  };

  const onDropRight = (e) => {
    e.preventDefault();
    try {
      const item = JSON.parse(e.dataTransfer.getData("item"));

      if (!rightList.find(i => i.id === item.id)) {
        setRightList([...rightList, item]);
        setLeftList(leftList.filter(i => i.id !== item.id));
      }
    } catch (error) {
      console.error("Error in drag drop:", error);
    }
  };

  const onDropLeft = (e) => {
    e.preventDefault();
    try {
      const item = JSON.parse(e.dataTransfer.getData("item"));

      if (!leftList.find(i => i.id === item.id)) {
        setLeftList([...leftList, item]);
        setRightList(rightList.filter(i => i.id !== item.id));
      }
    } catch (error) {
      console.error("Error in drag drop:", error);
    }
  };

  /* ================= OPEN ALLOCATION MODAL ================= */
  const handleOpenAllocation = async (groupId) => {
    setSelectedGroupId(groupId);
    setShowAllocation(true);
    setAllocationType("POS");
    setSelectedLeft([]);
    setSelectedRight([]);
    setSelectedSubGroupId("");
    setRightList([]);
    setOriginalRightList([]);
    setLeftList([]);
    setSubGroupOptions([]);

    // First fetch all available items, then fetch subgroups with the items
    const availableItems = await fetchAllAvailableItems();
    fetchSubGroups(groupId, availableItems);
  };

  /* ================= CLOSE ALLOCATION MODAL ================= */
  const handleCloseAllocation = () => {
    setShowAllocation(false);
    setSelectedGroupId(null);
    setLeftList([]);
    setRightList([]);
    setOriginalRightList([]);
    setSelectedLeft([]);
    setSelectedRight([]);
    setSubGroupOptions([]);
    setSelectedSubGroupId("");
    setAllAvailableItems([]);
  };

  /* ================= SAVE ALLOCATION ================= */
  const handleSaveAllocation = async () => {
    if (!selectedSubGroupId) {
      setMessage("Please select a subgroup first");
      return;
    }

    setLoading(true);
    try {
      // Find items to INSERT (items that are in rightList but were not in originalRightList)
      const itemsToInsert = rightList.filter(
        rightItem => !originalRightList.some(origItem => origItem.id === rightItem.id)
      );

      // Find items to DELETE (items that were in originalRightList but are now in leftList)
      const itemsToDelete = originalRightList.filter(
        origItem => !rightList.some(rightItem => rightItem.id === origItem.id)
      );

      // INSERT new allocations
      const insertPromises = itemsToInsert.map(item =>
        axios.post(
          "https://api.dqr-display-tms.bonrix.in/api/SubGroupDataOperation/Insert",
          {
            subgroupid: parseInt(selectedSubGroupId),
            posid: item.id,
            posname: item.posname || item.name,
            merchantid: item.merchantid,
            merchantname: item.merchantname,
            remark: item.remark || "",
            active: true
          },
          {
            headers: {
              "X-Authorization": token,
              UserId: userid,
              "Content-Type": "application/json-patch+json",
              accept: "text/plain",
            },
          }
        )
      );

      // DELETE removed allocations
      const deletePromises = itemsToDelete.map(item =>
        axios.post(
          "https://api.dqr-display-tms.bonrix.in/api/SubGroupDataOperation/Delete",
          {
            id: item.id
          },
          {
            headers: {
              "X-Authorization": token,
              UserId: userid,
              "Content-Type": "application/json-patch+json",
              accept: "text/plain",
            },
          }
        )
      );

      // Execute all operations
      await Promise.all([...insertPromises, ...deletePromises]);

      setMessage("Allocation saved successfully!");
      setTimeout(() => {
        handleCloseAllocation();
      }, 1500);

    } catch (error) {
      console.error("Error saving allocation:", error);
      setMessage("Failed to save allocation");
    } finally {
      setLoading(false);
    }
  };

  /* ================= FETCH GROUP DATA ================= */
  const fetchGroupData = async () => {
    setLoading(true);
    try {
      const GroupSearch = getUrlByOperation("GroupSearch") ||
        "https://api.dqr-display-tms.bonrix.in/api/GroupOperation/Search";

      const response = await axios.post(
        GroupSearch,
        {
          ispageload: false,
          pagesize: dataLimit,
          currentpageno: count,
          withoutpagination: false,
          pagedirection: "",
        },
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            "Content-Type": "application/json-patch+json",
            accept: "text/plain",
          },
        }
      );

      if (response.data && Array.isArray(response.data.data)) {
        setData(response.data.data);
        const rows = response.data.data;
        setHasMore(rows.length === dataLimit);
        const approximateTotal = (count * dataLimit) + rows.length + (rows.length === dataLimit ? dataLimit : 0);
        setTotalItems(approximateTotal);
      } else {
        setData([]);
        setHasMore(false);
        setTotalItems(0);
      }
    } catch (error) {
      console.error("Error fetching group data:", error);
      setMessage("Failed to fetch group data");
      setData([]);
      setHasMore(false);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroupData();
  }, [dataLimit, count]);

  const handlePageChange = (page) => {
    setCount(page - 1);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setDataLimit(newItemsPerPage);
    setCount(0);
  };

  const totalPages = hasMore ? count + 2 : count + 1;

  const handleAddGroup = () => {
    navigate("/addgroup");
  };

  const handleUpdateGroup = (id) => {
    navigate(`/updategroup/${id}`);
  };

  const handleViewSubGroup = (id) => {
    navigate(`/subgroup/${id}`);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this group?")) {
      try {
        const GroupDelete = getUrlByOperation("GroupDelete") ||
          "https://api.dqr-display-tms.bonrix.in/api/GroupOperation/Delete";

        await axios.post(
          GroupDelete,
          { id },
          {
            headers: {
              "X-Authorization": token,
              UserId: userid,
              "Content-Type": "application/json-patch+json",
              accept: "text/plain",
            },
          }
        );
        setMessage("Group deleted successfully!");
        fetchGroupData();
      } catch (error) {
        console.error("Error deleting group:", error);
        setMessage("Failed to delete group");
      }
    }
  };

  const handleToggle = async (item) => {
    try {
      const apiEndpoint = item.active
        ? "https://api.dqr-display-tms.bonrix.in/api/GroupOperation/UpdateStatus/Deactive"
        : "https://api.dqr-display-tms.bonrix.in/api/GroupOperation/UpdateStatus/Active";

      const GroupActive = getUrlByOperation("GroupActive");
      const GroupDeactive = getUrlByOperation("GroupDeactive");

      const url = item.active
        ? (GroupDeactive || apiEndpoint.replace("Active", "Deactive"))
        : (GroupActive || apiEndpoint);

      await axios.post(
        url,
        { id: item.id },
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            "Content-Type": "application/json-patch+json",
            accept: "text/plain",
          },
        }
      );
      setData(data.map((d) => (d.id === item.id ? { ...d, active: !item.active } : d)));
      setMessage(item.active ? "Group deactivated successfully!" : "Group activated successfully!");
    } catch (error) {
      console.error("Error updating group status:", error);
      setMessage("Failed to update group status");
      if (error.message === "relogin token expire") {
        navigate("/login");
      }
    }
  };

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  return (
    <>
      <Menu />
      <div className="wrapper">
        <div className="content-wrapper">
          <section className="content-header">
            <div className="container-fluid">
              <div className="d-flex justify-content-between align-items-center w-100">
                <h1 className="mb-0">Groups</h1>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleAddGroup}
                >
                  Add Group
                </button>
              </div>
            </div>
          </section>

          <section className="content">
            <div className="container-fluid">
              <hr />
              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-body table-responsive p-0">
                      <table className="table table-hover table-striped">
                         <thead className="" style={{
                            backgroundColor: 'var(--surface2)',
                            color: 'var(--text)',
                            borderColor: 'var(--border)'
                          }}>
                          <tr>
                            <th style={{ width: "5%" }} className="text-center">
                              ID
                            </th>
                            <th style={{ width: "20%" }}>Group Name</th>
                            <th style={{ width: "30%" }}>Description</th>
                            <th style={{ width: "20%" }} className="text-center">
                              Actions
                            </th>
                            <th style={{ width: "15%" }} className="text-center">
                              Active
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {loading ? (
                            <tr>
                              <td colSpan="5" className="text-center">
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                {" "}Loading...
                              </td>
                            </tr>
                          ) : data.length > 0 ? (
                            data.map((item, index) => (
                              <tr key={index}>
                                <td className="text-center">{item.id}</td>
                                <td>
                                  <strong>{item.groupname || item.name || "-"}</strong>
                                </td>
                                <td>{item.description || item.remark || "-"}</td>
                                <td className="text-center">
                                  <div className="btn-group">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-info mr-1"
                                      onClick={() => handleViewSubGroup(item.id)}
                                      title="View SubGroup"
                                    >
                                      <i className="fas fa-list"></i>
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-primary mr-1"
                                      onClick={() => handleUpdateGroup(item.id)}
                                      title="Edit Group"
                                    >
                                      <i className="fas fa-edit"></i>
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-danger mr-1"
                                      onClick={() => handleDelete(item.id)}
                                      title="Delete Group"
                                    >
                                      <i className="fas fa-trash"></i>
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-warning"
                                      style={{ color: "var(--text)" }}
                                      onClick={() => handleOpenAllocation(item.id)}
                                      title="Allocation"
                                    >
                                      Allocation
                                    </button>
                                  </div>
                                </td>
                                <td className="text-center">
                                  <Switch
                                    isOn={item.active}
                                    handleToggle={() => handleToggle(item)}
                                    id={item.id}
                                  />
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="5" className="text-center">
                                No groups found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    <Pagination
                      currentPage={count + 1}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      itemsPerPage={dataLimit}
                      onItemsPerPageChange={handleItemsPerPageChange}
                      totalItems={totalItems}
                      showPageNumbers={false}
                      showItemsPerPageSelector={true}
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* ALLOCATION MODAL */}
      {showAllocation && (
        <>
          <div
            onClick={handleCloseAllocation}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
              zIndex: 1040,
            }}
          />

          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "90%",
              maxWidth: "1200px",
              maxHeight: "90vh",
              background: "var(--surface)",
              padding: "20px",
              zIndex: 1050,
              borderRadius: "8px",
              boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
              overflowY: "auto"
            }}
          >
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h4 className="mb-0" style={{ color: 'var(--text)' }}>Allocation - Group ID: {selectedGroupId}</h4>
              <button
                className="btn btn-sm btn-danger"
                onClick={handleCloseAllocation}
                style={{ fontSize: "18px", padding: "5px 12px" }}
              >
                ×
              </button>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <label className="form-label font-weight-bold" style={{ color: 'var(--text)' }}>Search</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search POS or Merchant..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    backgroundColor: 'var(--surface2)',
                    color: 'var(--text)',
                    border: '1px solid var(--border)'
                  }}
                />
              </div>

              <div className="col-md-6">
                <label className="form-label font-weight-bold">
                  Select SubGroup <span className="text-danger">*</span>
                </label>
                <select
                  className="form-control"
                  value={selectedSubGroupId}
                  onChange={(e) => handleSubGroupChange(e.target.value)}
                >
                  <option value="">-- Select SubGroup --</option>
                  {subGroupOptions.map((subgroup) => (
                    <option key={subgroup.id} value={subgroup.id}>
                      {subgroup.subgroupname || subgroup.name || subgroup.groupname || `SubGroup ${subgroup.id}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <hr />

            <div className="row">
              <div className="col-md-5">
                <label className="font-weight-bold">
                  Available Items ({leftList.length})
                </label>
                <div
                  onDrop={onDropLeft}
                  onDragOver={(e) => e.preventDefault()}
                  style={{
                    border: "2px solid var(--border)",
                    borderRadius: "4px",
                    height: "400px",
                    overflowY: "auto",
                    padding: "10px",
                    backgroundColor: "var(--surface2)"
                  }}
                >
                  {leftList.length === 0 ? (
                    <div className="text-center text-muted py-5">
                      No items available
                    </div>
                  ) : (
                    leftList
                      .filter((item) => {
                        if (!searchTerm) return true;
                        const text = getDisplayText(item).toLowerCase();
                        return text.includes(searchTerm.toLowerCase());
                      })
                      .map((item) => (
                        <div
                          key={`left-${item.id}`}
                          draggable
                          onDragStart={(e) => onDragStart(e, item)}
                          className="p-2 mb-2 bg-white border rounded d-flex align-items-center"
                          style={{ cursor: "move" }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedLeft.includes(item.id)}
                            onChange={(e) =>
                              setSelectedLeft((prev) =>
                                e.target.checked
                                  ? [...prev, item.id]
                                  : prev.filter((id) => id !== item.id)
                              )
                            }
                            className="mr-2"
                          />
                          <div>
                            <strong>{getDisplayText(item)}</strong>
                            <br />
                            <small className="text-muted">
                              ID: {item.id}
                            </small>
                          </div>
                        </div>
                      ))
                  )}
                </div>
              </div>

              <div className="col-md-2 d-flex flex-column justify-content-center align-items-center">
                <button
                  className="btn btn-primary mb-3"
                  onClick={moveRight}
                  disabled={selectedLeft.length === 0 || !selectedSubGroupId}
                  style={{ width: "60px", height: "40px" }}
                  title="Move selected items to allocated"
                >
                  →
                </button>
                <button
                  className="btn btn-primary"
                  onClick={moveLeft}
                  disabled={selectedRight.length === 0}
                  style={{ width: "60px", height: "40px" }}
                  title="Move selected items back to available"
                >
                  ←
                </button>
              </div>

              <div className="col-md-5">
                <label className="font-weight-bold">
                  Allocated Items ({rightList.length})
                  {selectedSubGroupId && (
                    <span className="badge badge-info ml-2">
                      SubGroup ID: {selectedSubGroupId}
                    </span>
                  )}
                </label>
                <div
                  onDrop={onDropRight}
                  onDragOver={(e) => e.preventDefault()}
                  style={{
                    border: "2px solid var(--border)",
                    borderRadius: "4px",
                    height: "400px",
                    overflowY: "auto",
                    padding: "10px",
                    backgroundColor: "var(--surface2)"
                  }}
                >
                  {!selectedSubGroupId ? (
                    <div className="text-center text-warning py-5">
                      <i className="fas fa-info-circle fa-2x mb-2"></i>
                      <p>Please select a SubGroup first</p>
                    </div>
                  ) : rightList.length === 0 ? (
                    <div className="text-center text-muted py-5">
                      No items allocated to this subgroup
                    </div>
                  ) : (
                    rightList.map((item) => (
                      <div
                        key={`right-${item.id}`}
                        draggable
                        onDragStart={(e) => onDragStart(e, item)}
                        className="p-2 mb-2 bg-white border rounded d-flex align-items-center"
                        style={{ cursor: "move" }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedRight.includes(item.id)}
                          onChange={(e) =>
                            setSelectedRight((prev) =>
                              e.target.checked
                                ? [...prev, item.id]
                                : prev.filter((id) => id !== item.id)
                            )
                          }
                          className="mr-2"
                        />
                        <div>
                          <strong>{getDisplayText(item)}</strong>
                          <br />
                          <small className="text-muted">
                            ID: {item.id}
                          </small>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="row mt-3">
              <div className="col-12">
                <div className="alert alert-info">
                  <strong>Summary:</strong>
                  <span className="ml-2">Available: {leftList.length} items</span>
                  <span className="ml-3">Allocated: {rightList.length} items</span>
                  <span className="ml-3">Selected for Move: {selectedLeft.length + selectedRight.length} items</span>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-end mt-4">
              <button
                className="btn btn-secondary mr-2"
                onClick={handleCloseAllocation}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="btn btn-success"
                onClick={handleSaveAllocation}
                disabled={!selectedSubGroupId || loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : (
                  "Save Allocation"
                )}
              </button>
            </div>
          </div>
        </>
      )}

      {message && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
            zIndex: 9999,
            minWidth: "300px"
          }}
        >
          {message.includes("successfully") ? (
            <SuccessMessage message={message} />
          ) : message.includes("Failed") ? (
            <FailedMessage message={message} />
          ) : (
            <div
              className={`alert ${message.includes("successfully") ? "alert-success" : "alert-danger"
                }`}
              role="alert"
            >
              {message}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default SubGroup;

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}